import { Box, Divider, Typography, Link, CircularProgress, Grid } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import { useParams } from 'react-router-dom';
import TableContainer from '@mui/material/TableContainer';
import moment from 'moment';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { borderTop, display, fontSize } from '@mui/system';
import { isEmpty } from 'lodash';
import useMetaData from '../../hooks/useMetaData';
import { META_DATA_MODULES, META_DATA_SUB_MODULES } from '../../utils/constants';
import { payment, paymentService } from '../../services/vehicles';
import { PaymentData, PaymentsInter } from '../../types';
import { currencyFormat } from '../../utils/string';
import { useAppSelector } from '../../hooks/useRedux';
import GridLabels from '../VehicleTabs/GridLabels';

// handling record type
export const handleType = (
  type: string,
  item: any,
  keyItem: { id: string; fontSize?: number; fontWeight?: number; valueOnly?: boolean }
) => {
  switch (type) {
    case 'Amount':
      return keyItem?.valueOnly ? (
        currencyFormat(item?.[keyItem?.id], 'USD', 'US')
      ) : (
        <Typography fontSize={keyItem?.fontSize ?? 12} fontWeight={keyItem?.fontWeight ?? 400} color="#1B1B1B">
          {currencyFormat(item?.[keyItem?.id], 'USD', 'US')}
        </Typography>
      );
    case 'Link':
      return (
        <Link href="/" fontSize={keyItem?.fontSize ?? 12} color="#456BD9" sx={{ textDecoration: 'none' }}>
          {item?.[keyItem?.id]}
        </Link>
      );
    case 'Date':
      return keyItem?.valueOnly && item?.[keyItem?.id] ? (
        moment(item?.[keyItem?.id], 'Y-MM-DD hh:mm:ss')?.format('MMM D, Y')
      ) : (
        <Typography fontSize={keyItem?.fontSize ?? 12} fontWeight={keyItem?.fontWeight ?? 400} color="#1B1B1B">
          {item?.[keyItem?.id] ? moment(item?.[keyItem?.id])?.format('MMM D, Y') : '--'}
        </Typography>
      );
    default:
      return keyItem?.valueOnly ? (
        item?.[keyItem?.id]
      ) : (
        <Typography fontSize={keyItem?.fontSize ?? 12} fontWeight={keyItem?.fontWeight ?? 400} color="#1B1B1B">
          {item?.[keyItem?.id]}
        </Typography>
      );
  }
};

const Payments = () => {
  // use hooks
  const { reservationId } = useParams<{ reservationId: string }>();
  const paymentMetaData = useMetaData(META_DATA_MODULES.v360Page, META_DATA_SUB_MODULES.payment);

  // global states
  const { currentStage } = useAppSelector((state) => state.vehicle);

  // local states
  const [paymentData, setPaymentData] = useState<any | PaymentData>(null);

  // fetching record
  const fetchPaymentData = useCallback(async () => {
    try {
      const resp = await payment({ method: 'POST', data: { reservationId } });
      if (currentStage === 'Reserved') {
        const resp2 = await paymentService({ method: 'GET', url: reservationId });
        const trans: any = [];
        resp2?.data?.transactions?.map((item: any) => {
          return item?.payments?.map((sub: any) => {
            return trans?.push(sub);
          });
        });
        const sum = resp2?.data?.transactions?.reduce?.((accumulator: any, object: any) => {
          return accumulator + object.totalAmount;
        }, 0);
        setPaymentData({ ...resp?.data?.data, payments: trans, netPayments: sum });
      } else setPaymentData(resp?.data?.data);
    } catch (error) {
      console.log(error);
    }
  }, [reservationId, currentStage]);

  // lifecycle hooks
  useEffect(() => {
    if (paymentMetaData?.data?.staticData) {
      if (paymentMetaData?.data?.staticData === 'true')
        setPaymentData({
          netPayments: 51000,
          totalPayments: 51000,
          totalDue: 94596,
          totalAmount: 145596,
          currencyCode: 'USD',
          payments: [
            {
              paymentDate: new Date(),
              referenceId: 'P-253638',
              reason: '- -',
              method: 'Card',
              amount: 300,
            },
            {
              paymentDate: new Date(),
              referenceId: 'P-253639',
              reason: '- -',
              method: 'Card',
              amount: 700,
            },
            {
              paymentDate: new Date(),
              referenceId: 'P-253640',
              reason: '- -',
              method: 'Card',
              amount: 50000,
            },
          ],
        });
      else fetchPaymentData();
    }
  }, [fetchPaymentData, paymentMetaData]);

  if (!paymentData)
    return (
      <Box sx={{ position: 'relative', minHeight: '250px' }}>
        <CircularProgress
          color="inherit"
          size={18}
          sx={{ position: 'absolute', left: '0', right: '0', top: '50%', margin: '0 auto' }}
        />
      </Box>
    );
  return (
    <Box sx={{ width: '100%' }}>
      {paymentData?.[paymentMetaData?.data?.paymentSummary?.rows?.[0]?.id] > 0 ? (
        <>
          <Typography fontSize={20} fontWeight={500} lineHeight="24px">
            {/* {paymentMetaData?.data?.heading} */}
            {paymentMetaData?.data?.paymentSummary?.heading}
          </Typography>
          {/* - - payment summary - - - - */}
          <TableContainer sx={{ mt: 1.2, border: '1px solid #e3e3e3', borderRadius: '4px', borderBottom: '0' }}>
            <Table
              aria-label="payment summary table"
              sx={{ borderCollapse: 'collapse', borderRadius: '4px', overflow: 'hidden', width: '100%' }}
            >
              <TableBody>
                {paymentMetaData?.data?.paymentSummary?.rows?.map(
                  (item: { id: string; name: string; type: string }) => {
                    return (
                      <TableRow
                        key={item?.id}
                        sx={{
                          display:
                            paymentData?.financeTypeDetails?.financedWith !== 'Lucid Motors' &&
                            item.id === 'expectedFinancingAmount'
                              ? 'none'
                              : '',
                        }}
                      >
                        <TableCell
                          component="td"
                          scope="row"
                          sx={{
                            '&:first-child': { borderLeft: 0 },
                            '&:last-child': { borderBottom: 0 },
                            padding: '10px',
                            background: item.id === 'totalDue' ? '#f2f2f2' : '#fff',
                          }}
                        >
                          <Typography
                            color="#1B1B1B"
                            fontSize={item?.id === 'totalDue' ? 14 : 12}
                            fontWeight={item?.id === 'totalDue' ? 700 : 400}
                          >
                            {item?.name}
                          </Typography>
                        </TableCell>
                        <TableCell
                          component="td"
                          scope="row"
                          align="right"
                          sx={{
                            background: item.id === 'totalDue' ? '#f2f2f2' : '#fff',
                            padding: '8px 10px',
                          }}
                        >
                          <Typography
                            color="#1B1B1B"
                            fontSize={item?.id === 'totalDue' ? 14 : 12}
                            fontWeight={item?.id === 'totalDue' ? 700 : 400}
                          >
                            {item?.type === 'Amount'
                              ? currencyFormat(paymentData?.[item?.id], 'USD', 'US')
                              : paymentData?.[item?.id] ?? '--'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  }
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null}

      {/* - - Financing - - - - -  */}
      <Box sx={{ width: '100%', my: 4 }}>
        <Typography fontSize={14} mb={1} fontWeight={500}>
          Financing
        </Typography>
        <Divider sx={{ borderColor: '#e3e3e3', borderStyle: 'dashed' }} />
        <Grid container spacing={2} mt={0}>
          {isEmpty(paymentData?.financeTypeDetails) && (
            <GridLabels
              labels={[{ label: 'Financed With', title: 'No Financing' }]}
              labelSx={{ fontSize: '14px', fontWeight: '400', color: '#717171' }}
              titleSx={{ fontSize: '16px', fontWeight: '500', color: '#3E3E3E' }}
            />
          )}

          {!isEmpty(paymentData?.financeTypeDetails) &&
            paymentData?.financeTypeDetails?.financedWith === 'Lucid Motors' && (
              <GridLabels
                labels={[
                  { label: 'Financed With', title: paymentData?.financeTypeDetails?.financedWith ?? '--' },
                  {
                    label: 'Financing Ready for HandOff',
                    title: paymentData?.financeTypeDetails?.financingReadyForHandOff ?? '--',
                  },
                  {
                    label: 'Status',
                    title: paymentData?.financeTypeDetails?.status ?? '--',
                    type: paymentData?.financeTypeDetails?.status === 'Approved' ? 'Success' : 'Status',
                  },
                  {
                    label: 'Amount',
                    title: currencyFormat(paymentData?.financeTypeDetails?.amount, 'USD', 'US'),
                  },
                ]}
                labelSx={{ fontSize: '14px', fontWeight: '400', color: '#717171' }}
                titleSx={{ fontSize: '16px', fontWeight: '500', color: '#3E3E3E' }}
              />
            )}

          {!isEmpty(paymentData?.financeTypeDetails) &&
            paymentData?.financeTypeDetails?.financedWith === '3rd Party Financing' && (
              <GridLabels
                labels={[
                  { label: 'Financed With', title: paymentData?.financeTypeDetails?.financedWith ?? '--' },
                  {
                    label: '3rd Party Financing Amount',
                    title: currencyFormat(paymentData?.financeTypeDetails?.financingAmt, 'USD', 'US'),
                  },
                  { label: 'Lien Holder Name', title: paymentData?.financeTypeDetails?.lienHolderName ?? '--' },
                  { label: 'Lien Holder Street', title: paymentData?.financeTypeDetails?.lienHolderStreet ?? '--' },
                  { label: 'Lien Holder City', title: paymentData?.financeTypeDetails?.lienHolderCity ?? '--' },
                  {
                    label: 'Lien Holder State/Province',
                    title: paymentData?.financeTypeDetails?.lienHolderState ?? '--',
                  },
                  { label: 'Lien Holder Zip/Postal Code', title: paymentData?.financeTypeDetails?.lienPostal ?? '--' },
                  { label: 'Lien Holder Country', title: paymentData?.financeTypeDetails?.lienCountry ?? '--' },
                ]}
                labelSx={{ fontSize: '14px', fontWeight: '400', color: '#717171' }}
                titleSx={{ fontSize: '16px', fontWeight: '500', color: '#3E3E3E' }}
              />
            )}
        </Grid>
      </Box>

      {/* - - payment History - - - - */}
      <Typography fontSize={14} fontWeight={500} py={1} mt={3}>
        {paymentMetaData?.data?.paymentHistory?.heading}
      </Typography>
      <TableContainer sx={{ my: 0.5 }}>
        <Table aria-label="payment history table">
          <TableHead>
            <TableRow>
              {paymentMetaData?.data?.paymentHistory?.tableHeading?.map((item: { id: string; name: string }) => {
                return (
                  <TableCell
                    key={item?.id}
                    sx={{
                      background: '#f9f9f9',
                      padding: '8px 10px',
                      fontSize: '12px',
                      fontWeight: '500',
                      borderBottom: '0',
                      textAlign: 'left',
                      '&:last-child': { textAlign: 'right' },
                    }}
                  >
                    <Typography fontSize={12} fontWeight={500} color="#555354">
                      {item?.name}
                    </Typography>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {paymentData?.payments?.map((item: any) => {
              return (
                <TableRow key={item?._id}>
                  {currentStage === 'Reserved'
                    ? paymentMetaData?.data?.paymentHistory?.tableHeadingSecond?.map(
                        (keyItem: { id: string; type: string }) => {
                          return (
                            <TableCell
                              component="td"
                              scope="row"
                              sx={{ padding: '8px 10px', textAlign: 'left', '&:last-child': { textAlign: 'right' } }}
                            >
                              {handleType(keyItem.type, item, keyItem)}
                            </TableCell>
                          );
                        }
                      )
                    : paymentMetaData?.data?.paymentHistory?.tableHeading?.map(
                        (keyItem: { id: string; type: string }) => {
                          return (
                            <TableCell
                              component="td"
                              scope="row"
                              sx={{ padding: '8px 10px', textAlign: 'left', '&:last-child': { textAlign: 'right' } }}
                            >
                              {handleType(keyItem.type, item, keyItem)}
                            </TableCell>
                          );
                        }
                      )}
                </TableRow>
              );
            })}
            {paymentMetaData?.data?.paymentSummary?.rows
              ?.filter((fil: { id: string }) => fil?.id === 'netPayments')
              ?.map((item: { id: string; name: string; type: string }) => {
                return (
                  <TableRow key={item?.id}>
                    <TableCell
                      colSpan={5}
                      component="td"
                      scope="row"
                      sx={{
                        '&:first-child': { borderLeft: 0 },
                        '&:last-child': { borderBottom: 0 },
                        padding: '8px 10px',
                        background: '#f2f2f2',
                      }}
                    >
                      <Typography color="#1B1B1B" fontSize={14} fontWeight={700}>
                        {item?.name}
                      </Typography>
                    </TableCell>
                    <TableCell
                      component="td"
                      scope="row"
                      sx={{
                        '&:first-child': { borderLeft: 0 },
                        padding: '8px 10px',
                        background: '#f2f2f2',
                      }}
                    >
                      <Typography color="#1B1B1B" fontSize={14} fontWeight={700} textAlign="right">
                        {item?.type === 'Amount'
                          ? currencyFormat(paymentData?.[item?.id], 'USD', 'US')
                          : paymentData?.[item?.id] ?? '--'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Payments;
